import type { Command, Connection, ConnectionId, ConnectionType, ObjectId, WorkspaceDocument } from '@/types';
import { asConnectionId } from '@/types';
import { createCommand } from '@/core/commands/createCommand';
import { findBetween, isSymmetric, wouldCreateCycle } from '@/core/graph/connectionGraph';
import { createId } from '@/utils/id';

type Doc = WorkspaceDocument;

const omit = (record: Doc['connections'], ids: ReadonlySet<ConnectionId>): Doc['connections'] =>
  Object.fromEntries(Object.entries(record).filter(([k]) => !ids.has(k as ConnectionId)));

const replace = (d: Doc, c: Connection): Doc => ({ ...d, connections: { ...d.connections, [c.id]: c } });

export type ConnectionRejection = 'missing-object' | 'self-link' | 'duplicate' | 'cycle';

/** Why a proposed edge is not allowed, or null when it is. */
export const validateEdge = (
  doc: Doc,
  sourceId: ObjectId,
  targetId: ObjectId,
  type: ConnectionType,
  ignoreId?: ConnectionId,
): ConnectionRejection | null => {
  if (!doc.objects[sourceId] || !doc.objects[targetId]) return 'missing-object';
  if (sourceId === targetId) return 'self-link';
  const existing = findBetween(doc.connections, sourceId, targetId);
  if (existing && existing.id !== ignoreId) return 'duplicate';
  if (wouldCreateCycle(doc.connections, sourceId, targetId, type, ignoreId)) return 'cycle';
  return null;
};

export const addConnectionCommand = (
  doc: Doc,
  sourceId: ObjectId,
  targetId: ObjectId,
  options: { readonly type?: ConnectionType; readonly label?: string; readonly id?: ConnectionId } = {},
): Command<Doc> | null => {
  const type = options.type ?? 'linked-to';
  if (validateEdge(doc, sourceId, targetId, type)) return null;
  const connection: Connection = {
    id: options.id ?? asConnectionId(createId('con')),
    workspaceId: doc.workspace.id,
    sourceId,
    targetId,
    type,
    label: options.label ?? '',
  };
  return createCommand<Doc>({
    name: 'connection.add',
    label: 'Connect objects',
    execute: (d) => replace(d, connection),
    undo: (d) => ({ ...d, connections: omit(d.connections, new Set([connection.id])) }),
  });
};

export const deleteConnectionsCommand = (doc: Doc, ids: readonly ConnectionId[]): Command<Doc> | null => {
  const removed = ids.map((id) => doc.connections[id]).filter((c): c is Connection => c !== undefined);
  if (!removed.length) return null;
  const removedIds = new Set(removed.map((c) => c.id));
  return createCommand<Doc>({
    name: 'connection.delete',
    label: removed.length === 1 ? 'Delete connection' : `Delete ${removed.length} connections`,
    execute: (d) => ({ ...d, connections: omit(d.connections, removedIds) }),
    undo: (d) => ({ ...d, connections: { ...d.connections, ...Object.fromEntries(removed.map((c) => [c.id, c])) } }),
  });
};

const update = (
  doc: Doc,
  id: ConnectionId,
  name: string,
  label: string,
  patch: Partial<Connection>,
): Command<Doc> | null => {
  const before = doc.connections[id];
  if (!before) return null;
  const after: Connection = { ...before, ...patch, id: before.id, workspaceId: before.workspaceId };
  if (
    after.sourceId === before.sourceId &&
    after.targetId === before.targetId &&
    after.type === before.type &&
    after.label === before.label
  )
    return null;
  if (validateEdge(doc, after.sourceId, after.targetId, after.type, id)) return null;
  return createCommand<Doc>({ name, label, execute: (d) => replace(d, after), undo: (d) => replace(d, before) });
};

/** Moves one end of a connection to another object. */
export const reconnectCommand = (
  doc: Doc,
  id: ConnectionId,
  end: 'source' | 'target',
  objectId: ObjectId,
): Command<Doc> | null =>
  update(
    doc,
    id,
    'connection.reconnect',
    'Reconnect',
    end === 'source' ? { sourceId: objectId } : { targetId: objectId },
  );

export const setConnectionTypeCommand = (doc: Doc, id: ConnectionId, type: ConnectionType): Command<Doc> | null =>
  update(doc, id, 'connection.type', 'Change relationship', { type });

export const setConnectionLabelCommand = (doc: Doc, id: ConnectionId, label: string): Command<Doc> | null =>
  update(doc, id, 'connection.label', 'Label connection', { label: label.trim() });

/** Swaps direction. Symmetric types are unchanged in meaning but the record still flips. */
export const reverseConnectionCommand = (doc: Doc, id: ConnectionId): Command<Doc> | null => {
  const c = doc.connections[id];
  if (!c) return null;
  return update(doc, id, 'connection.reverse', isSymmetric(c.type) ? 'Swap ends' : 'Reverse direction', {
    sourceId: c.targetId,
    targetId: c.sourceId,
  });
};
